import React, { FC, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useCartModelDisplay from '../../../../../hooks/useCartModelDisplay';
import Product from '../../../../../@types/Product';
import ProductImageSelect from '../../../../common/Form/FormFields/ProductImageSelect/ProductImageSelect';

interface CartProductSeatsProps {
  retailConfigurator: boolean,
  hideAllPrices: boolean,
}

const CartProductSeats: FC<CartProductSeatsProps> = ({ retailConfigurator, hideAllPrices }) => {
  const [matchingFrontSeats, setMatchingFrontSeats] = useState<Product[]>([]);
  const { setValue, getValues } = useFormContext();
  const { chassisType, setFrontSeat, setRearSeat } = useCartModelDisplay();
  const bodyColor: Product | undefined = useWatch({ name: 'bodyColor' });
  const frontSeat: Product | undefined = useWatch({ name: 'frontSeat' });
  const currentCart = getValues();
  const editable = currentCart?.isFromInventory
      ? (currentCart?.seatsEditable ?? false)
      : true;

  // Filters options based refId & associated bodyColorSku
  useEffect(() => {
    const availableFrontSeatOptions = chassisType?.frontSeatOptions.filter((product) => {
      if (Array.isArray(product.refId)) {
        return [...product.refId].includes(bodyColor?.sku ?? '');
      } else {
        return product.refId === bodyColor?.sku;
      }
    });

    const availableRearSeatOptions = chassisType?.rearSeatOptions?.filter((product) => {
      if (Array.isArray(product.refId)) {
        return [...product.refId].includes(bodyColor?.sku ?? '');
      } else {
        return product.refId === bodyColor?.sku;
      }
    });

    setMatchingFrontSeats(availableFrontSeatOptions ?? []);

    if (availableFrontSeatOptions) {
      // If only one front seat option
      if (availableFrontSeatOptions?.length === 1) {
        // set seat for cart model display
        setFrontSeat(availableFrontSeatOptions[0]);
        setValue('frontSeat', availableFrontSeatOptions[0]);
      } else {
        // Get current frontSeast if exists
        const frontSeat: Product | undefined = getValues('frontSeat');

        // Get current bodyColor product if exists
        const bodyColor: Product | undefined  = getValues('bodyColor');

        /*
           If no front seat option or body color option is selected
           we want to color match when a body color is selected
       */

        let autoColorMatch = false;

        if (Array.isArray(frontSeat?.refId)) {
          autoColorMatch = !frontSeat?.refId.some((refId) => refId === bodyColor?.sku);
        } else {
          autoColorMatch = !(frontSeat?.refId === bodyColor?.sku);
        }

        if (autoColorMatch) {
          // Find matching color option or default to first option
          const colorMatchingFrontSeat = availableFrontSeatOptions?.find((product) => product.refId === bodyColor?.sku);
          if (colorMatchingFrontSeat) {
            setFrontSeat(colorMatchingFrontSeat);
            setValue('frontSeat', colorMatchingFrontSeat);
          } else {
            setFrontSeat(availableFrontSeatOptions[0]);
            setValue('frontSeat', availableFrontSeatOptions[0]);
          }
        }
      }

    }

    // If only one rear seat option
    if (availableRearSeatOptions?.length === 1) {
      // set seat for cart model display
      setRearSeat(availableRearSeatOptions[0]);
    } else {

      // Get current frontSeast if exists
      const frontSeat: Product | undefined = getValues('frontSeat');

      // Get current bodyColor product if exists
      const bodyColor: Product | undefined = getValues('bodyColor');

      /*
         If no front seat option or body color option is selected
         we want to color match when a body color is selected
     */

      let autoColorMatch = false;

      if (Array.isArray(frontSeat?.refId)) {
        autoColorMatch = !frontSeat?.refId.some((refId) => refId === bodyColor?.sku);
      } else {
        autoColorMatch = !(frontSeat?.refId === bodyColor?.sku);
      }
      if (autoColorMatch) {
        // Find matching color option or default to first option
        const colorMatchingRearSeat = availableRearSeatOptions?.find((product) => product.refId === bodyColor?.sku);
        if (colorMatchingRearSeat) {
          setRearSeat(colorMatchingRearSeat);
        } else {
          if (availableRearSeatOptions?.length) {
            setRearSeat(availableRearSeatOptions[0]);
          }
        }
      }
    }

  }, [
    chassisType,
    bodyColor,
    setMatchingFrontSeats,
    setFrontSeat,
    setRearSeat,
    getValues,
    setValue
  ]);



  useEffect(() => {
    // Find matching middle & rear seat when exists
    const middleSeat: Product | undefined = chassisType?.middleSeatOptions?.find((product) => product.id === frontSeat?.id);
    const rearSeat: Product | undefined = chassisType?.rearSeatOptions?.find((product) => product.id === frontSeat?.id);
    setValue('middleSeat', middleSeat);
    setValue('rearSeat', rearSeat);
    setFrontSeat(frontSeat);
    setRearSeat(rearSeat);
  }, [
    frontSeat,
    chassisType,
    setValue,
    setFrontSeat,
    setRearSeat
  ]);

  return (
    <ProductImageSelect
      disableImageZoom={true}
      name={'frontSeat'}
      label={'Seat Options'}
      displayPrice={false}
      displayLabel={false}
      disabled={!editable}
      productAlignment='left'
      displayRetailPrice={retailConfigurator}
      products={matchingFrontSeats ?? []} />
  );
}

export default CartProductSeats;
